import React, { useState, useEffect, useRef, useCallback } from "react";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";
import Alert from "./Alert";
import BuySpinsModal from "./BuySpinsModal";
import { useProfile, useProfileRefresh } from "../contexts/ProfileContext";
import { useLanguage } from "../contexts/LanguageContext";

// ─── Wheel layout — MUST match server WEDGES in routes/dailybonus.js ────────
const WEDGES = [
  { value: 1000, gold: true,  honeypot: true  }, // 0 — visual lure, never lands
  { value: 5,    gold: false, honeypot: false }, // 1
  { value: 50,   gold: true,  honeypot: false }, // 2
  { value: 1,    gold: false, honeypot: false }, // 3
  { value: 10,   gold: false, honeypot: false }, // 4
  { value: 2,    gold: false, honeypot: false }, // 5
  { value: 25,   gold: true,  honeypot: false }, // 6
  { value: 3,    gold: false, honeypot: false }, // 7
  { value: 7,    gold: false, honeypot: false }, // 8
];
const N = WEDGES.length;
const SEG = 360 / N;
const R = 168;
const polar = (radius, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [radius * Math.cos(rad), radius * Math.sin(rad)];
};

// ─── Rarity ─────────────────────────────────────────────────────────────────
function getRarity(amount) {
  if (amount >= 25) return "legendary";
  if (amount >= 10) return "epic";
  if (amount >= 5)  return "rare";
  return "common";
}

// Celebration titles live in the language files (L.dailyBonus.celebrations).
const WIN_EMOJIS = {
  common:    ["🎉", "✨", "🎊"],
  rare:      ["🎉", "💫", "⭐"],
  epic:      ["🔥", "🌟", "💥"],
  legendary: ["👑", "💎", "🏆"],
};
const pickFrom = (a) => a[Math.floor(Math.random() * a.length)];

// ─── Haptic patterns (Android vibration + iOS Taptic via hidden inputs) ─────
const HAPTIC = {
  tap: 25,
  bump: 50,
  thump: [140, 35, 90, 35, 70],
  winCommon:    [60, 60, 80, 60, 100],
  winRare:      [55, 50, 65, 50, 80, 50, 100],
  winEpic:      [50, 45, 55, 45, 60, 45, 70, 45, 90, 45, 130],
  winLegendary: [40, 40, 40, 40, 40, 40, 50, 40, 60, 40, 80, 40, 110, 40, 150, 40, 200],
};

function useHapticsAudio() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const spinBufRef = useRef(null);
  const winBufRef = useRef(null);
  const spinSrcRef = useRef(null);
  const hapticPoolRef = useRef([]);
  const hapticIdxRef = useRef(0);
  const lastHapticRef = useRef(0);

  const ensureCtx = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try {
      ctxRef.current = new AC();
      masterRef.current = ctxRef.current.createGain();
      masterRef.current.gain.value = 0.72;
      masterRef.current.connect(ctxRef.current.destination);
    } catch {
      return null;
    }
    return ctxRef.current;
  }, []);

  const loadBuffers = useCallback(async () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const decode = async (url) => {
      try {
        const r = await fetch(url);
        const b = await r.arrayBuffer();
        return await new Promise((res, rej) => ctx.decodeAudioData(b, res, rej));
      } catch {
        return null;
      }
    };
    if (!spinBufRef.current) spinBufRef.current = await decode("/audio/wheel-spin.mp3");
    if (!winBufRef.current)  winBufRef.current  = await decode("/audio/win-sound.mp3");
  }, [ensureCtx]);

  useEffect(() => {
    ensureCtx();
    loadBuffers();
    const wake = () => {
      const ctx = ensureCtx();
      if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
      loadBuffers();
    };
    ["pointerdown", "touchstart", "click", "keydown"].forEach(evt =>
      document.addEventListener(evt, wake, { capture: true, passive: true })
    );
    // Build iOS haptic pool
    if (typeof document !== "undefined" && document.body && hapticPoolRef.current.length === 0) {
      for (let i = 0; i < 4; i++) {
        const label = document.createElement("label");
        Object.assign(label.style, {
          position: "fixed", left: "-9999px", top: "-9999px",
          opacity: "0.01", pointerEvents: "none",
        });
        const input = document.createElement("input");
        input.type = "checkbox";
        input.setAttribute("switch", "");
        label.appendChild(input);
        document.body.appendChild(label);
        hapticPoolRef.current.push(label);
      }
    }
    return () => {
      ["pointerdown", "touchstart", "click", "keydown"].forEach(evt =>
        document.removeEventListener(evt, wake, { capture: true })
      );
    };
  }, [ensureCtx, loadBuffers]);

  const iosHaptic = useCallback(() => {
    const now = performance.now();
    if (now - lastHapticRef.current < 80) return;
    lastHapticRef.current = now;
    const pool = hapticPoolRef.current;
    if (!pool.length) return;
    const lbl = pool[hapticIdxRef.current % pool.length];
    hapticIdxRef.current++;
    try { lbl.click(); } catch { /* noop */ }
  }, []);

  const vibrate = useCallback((pattern) => {
    if (navigator.vibrate) {
      try { navigator.vibrate(pattern); } catch { /* noop */ }
    }
    if (Array.isArray(pattern)) {
      let elapsed = 0;
      pattern.forEach((dur, i) => {
        if (i % 2 === 0) setTimeout(iosHaptic, elapsed);
        elapsed += dur;
      });
    } else {
      iosHaptic();
    }
  }, [iosHaptic]);

  const tone = useCallback(({ freq, dur, type = "sine", gain = 0.15, attack = 0.003, slide }) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(slide, 1), t + dur);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(masterRef.current);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }, []);

  const tick = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.state !== "running") return;
    const t = ctx.currentTime;
    const dur = 0.03;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.6);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filt = ctx.createBiquadFilter();
    filt.type = "bandpass";
    filt.frequency.value = 1100;
    filt.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.32, t);
    g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
    src.connect(filt).connect(g).connect(masterRef.current);
    src.start(t);
  }, []);

  const playSpinAudio = useCallback((durationMs) => {
    const ctx = ensureCtx();
    if (!ctx || !spinBufRef.current) return;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
    if (spinSrcRef.current) { try { spinSrcRef.current.stop(); } catch {} }
    const SPIN_DUR = 5.59;
    const fullSpinSec = durationMs / 1000;
    const offset = Math.max(0, SPIN_DUR - fullSpinSec);
    const playableDur = Math.min(fullSpinSec, SPIN_DUR - offset);
    const src = ctx.createBufferSource();
    src.buffer = spinBufRef.current;
    const g = ctx.createGain();
    g.gain.value = 0.30;
    src.connect(g).connect(masterRef.current);
    try { src.start(ctx.currentTime, offset, playableDur); } catch {}
    spinSrcRef.current = src;
  }, [ensureCtx]);

  const playWinAudio = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx || !winBufRef.current) return;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch {} }
    const src = ctx.createBufferSource();
    src.buffer = winBufRef.current;
    const g = ctx.createGain();
    g.gain.value = 0.32;
    src.connect(g).connect(masterRef.current);
    try { src.start(ctx.currentTime); } catch {}
  }, [ensureCtx]);

  return { vibrate, tone, tick, playSpinAudio, playWinAudio };
}

const WEDGE_FILL = (i) => {
  if (WEDGES[i].honeypot || WEDGES[i].gold) return "url(#dbGoldGrad)";
  return i % 2 === 0 ? "#7c3aed" : "#635bff";
};

function DailyBonus() {
  const L = useLanguage();
  const profile = useProfile();
  const refreshProfile = useProfileRefresh();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0);
  const [resultModal, setResultModal] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [error, setError] = useState("");
  const stageRef = useRef(null);

  const { vibrate, tick, playSpinAudio, playWinAudio } = useHapticsAudio();

  const loadStatus = useCallback(async () => {
    try {
      const res = await axiosClient.get(`${API_BASE_URL}/dailybonus/status`);
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Start ticking sounds at decelerating intervals during the spin
  const startTicking = useCallback((durationMs) => {
    let elapsed = 0;
    let stopped = false;
    const tickOnce = () => {
      if (stopped) return;
      const progress = elapsed / durationMs;
      if (progress >= 1) return;
      tick();
      if (progress > 0.85) vibrate(HAPTIC.bump);
      // Interval grows as progress increases — fast at start, slow at end
      const interval = 30 + Math.pow(progress, 2.4) * 320;
      elapsed += interval;
      setTimeout(tickOnce, interval);
    };
    tickOnce();
    return () => { stopped = true; };
  }, [tick, vibrate]);

  const screenShake = useCallback((ms) => {
    if (!stageRef.current) return;
    stageRef.current.classList.add("db-shake");
    setTimeout(() => stageRef.current?.classList.remove("db-shake"), ms);
  }, []);

  const celebrate = useCallback((rarity) => {
    if (rarity === "legendary") screenShake(320);
    else if (rarity === "epic") screenShake(220);
    else if (rarity === "rare") screenShake(140);
    const hap = {
      legendary: HAPTIC.winLegendary, epic: HAPTIC.winEpic,
      rare: HAPTIC.winRare, common: HAPTIC.winCommon,
    }[rarity];
    vibrate(hap);
    // Canvas confetti for epic+
    if ((rarity === "epic" || rarity === "legendary") && window.confetti) {
      const opts = {
        particleCount: rarity === "legendary" ? 220 : 120,
        spread: rarity === "legendary" ? 100 : 75,
        origin: { x: 0.5, y: 0.6 },
        colors: rarity === "legendary"
          ? ["#ffd24a", "#f5a623", "#635bff", "#d345c6"]
          : ["#635bff", "#7c3aed", "#d345c6"],
      };
      window.confetti(opts);
    } else if ((rarity === "epic" || rarity === "legendary") && !window.confetti) {
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js";
      s.onload = () => window.confetti?.({
        particleCount: rarity === "legendary" ? 220 : 120,
        spread: rarity === "legendary" ? 100 : 75,
        origin: { x: 0.5, y: 0.6 },
      });
      document.head.appendChild(s);
    }
  }, [vibrate, screenShake]);

  const handleSpin = async () => {
    if (spinning || !status?.eligible) return;
    const totalSpins = (status.freeSpinAvailable ? 1 : 0) + (status.paidSpins || 0);
    if (totalSpins <= 0) return;

    setSpinning(true);
    setError("");
    vibrate(HAPTIC.tap);

    try {
      const res = await axiosClient.post(`${API_BASE_URL}/dailybonus/spin`);
      const { wedgeIndex, amount } = res.data;

      // Land target with near-miss jitter
      const baseTarget = (360 - wedgeIndex * SEG) % 360;
      const jitterPct = 0.10 + Math.random() * 0.32;
      const jitterDir = Math.random() < 0.5 ? 1 : -1;
      const jitter = SEG * jitterPct * jitterDir;
      const turns = 6 + Math.floor(Math.random() * 3);
      const next = angle + turns * 360 + baseTarget + jitter - (angle % 360);
      const SPIN_MS = 5500;
      setAngle(next);
      playSpinAudio(SPIN_MS);
      const stopTicks = startTicking(SPIN_MS);

      setTimeout(() => {
        stopTicks();
        setSpinning(false);
        vibrate(HAPTIC.thump);
        const rarity = getRarity(amount);
        celebrate(rarity);
        playWinAudio();
        setResultModal({
          amount,
          isFree: res.data.type === "free",
          rarity,
          title: pickFrom(L.dailyBonus.celebrations[rarity]),
          emoji: pickFrom(WIN_EMOJIS[rarity]),
        });
        loadStatus();
        refreshProfile();
      }, SPIN_MS + 80);
    } catch (err) {
      setSpinning(false);
      setError(err.response?.data?.error || err.message);
    }
  };

  const [buyDefault, setBuyDefault] = useState("pack");
  const openBuy = (pack = "pack") => {
    vibrate(HAPTIC.bump);
    setBuyDefault(pack);
    setBuyOpen(true);
  };

  if (!status) {
    return <div className="text-center py-12 stripe-text-muted">{L.dailyBonus.loading}</div>;
  }

  const totalSpins = (status.freeSpinAvailable ? 1 : 0) + (status.paidSpins || 0);
  const canSpin = totalSpins > 0 && !spinning;
  const idlePulse = canSpin && !spinning;

  return (
    <div className="db-stage" ref={stageRef}>
      {error && <Alert variant="danger" message={error} show={!!error} onClose={() => setError("")} />}

      <h1 className="db-heading">{L.dailyBonus.heading}</h1>
      <p className="db-sub">{L.dailyBonus.sub}</p>

      <div className="db-badges-row">
        <span className="db-badge-pill">
          <span className="db-count">{totalSpins}</span>
          {totalSpins === 1 ? L.dailyBonus.spinAvailable : L.dailyBonus.spinsAvailable}
        </span>
        {status.freeSpinAvailable ? (
          <span className="db-badge-pill db-badge-pill--free">
            <span className="db-count db-count--free">{L.dailyBonus.free}</span>
            {L.dailyBonus.dailySpinReady}
          </span>
        ) : !status.freeSpinLocked ? (
          <span className="db-badge-pill db-badge-pill--muted">{L.dailyBonus.nextFreeSpin}</span>
        ) : null}
      </div>

      {status.freeSpinLocked && (
        <div className="db-locked-banner">
          <div className="db-locked-icon" aria-hidden>🔒</div>
          <div className="db-locked-text">
            <div className="db-locked-title">{L.dailyBonus.lockedTitle}</div>
            <div className="db-locked-sub">{L.dailyBonus.lockedSub}</div>
          </div>
          <a href="/dashboard/upgrade" className="db-locked-cta">{L.dailyBonus.upgrade}</a>
        </div>
      )}

      <div className={`db-wheel-wrap ${idlePulse ? "db-wheel-wrap--idle" : ""}`}>
        <div className="db-bezel">
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i * 360 / 24) - 90;
            const [x, y] = polar(R + 26, a);
            return (
              <div key={i} className="db-stud"
                style={{ transform: `translate(${x}px, ${y}px)` }} />
            );
          })}
        </div>
        <div className="db-pointer">
          <svg viewBox="0 0 32 40" width="32" height="40">
            <defs>
              <linearGradient id="dbPointerGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff8c42" />
                <stop offset="100%" stopColor="#ff5722" />
              </linearGradient>
            </defs>
            <path d="M16 38 L2 6 Q16 14 30 6 Z" fill="url(#dbPointerGrad)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </div>
        <svg
          className={`db-wheel ${spinning ? "db-wheel--spinning" : ""}`}
          viewBox={`-${R + 20} -${R + 20} ${(R + 20) * 2} ${(R + 20) * 2}`}
          style={{
            transform: `rotate(${angle}deg)`,
            transition: spinning
              ? "transform 5.5s cubic-bezier(.16,.73,.12,1)"
              : "none",
          }}
        >
          <defs>
            <linearGradient id="dbGoldGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffd24a" />
              <stop offset="100%" stopColor="#f5a623" />
            </linearGradient>
            <radialGradient id="dbHubGrad" cx="0.35" cy="0.35" r="0.7">
              <stop offset="0%" stopColor="#fff" />
              <stop offset="100%" stopColor="#e0d7ff" />
            </radialGradient>
          </defs>
          {WEDGES.map((w, i) => {
            const startDeg = i * SEG - SEG / 2;
            const endDeg = startDeg + SEG;
            const [x1, y1] = polar(R, startDeg);
            const [x2, y2] = polar(R, endDeg);
            const labelDeg = i * SEG;
            const [lx, ly] = polar(R * 0.62, labelDeg);
            return (
              <g key={i}>
                <path
                  d={`M0,0 L${x1},${y1} A${R},${R} 0 0 1 ${x2},${y2} Z`}
                  fill={WEDGE_FILL(i)}
                  stroke="#fff" strokeWidth="2" strokeLinejoin="round"
                />
                <text
                  x={lx} y={ly}
                  fill={w.gold ? "#7a4a00" : "#fff"}
                  fontFamily="Baloo 2, Fredoka, sans-serif"
                  fontWeight="800"
                  fontSize={w.value >= 100 ? 22 : 28}
                  textAnchor="middle" dominantBaseline="middle"
                  transform={`rotate(${labelDeg} ${lx} ${ly})`}
                  paintOrder="stroke"
                  stroke="rgba(0,0,0,0.18)" strokeWidth="0.8"
                >
                  ${w.value}
                </text>
              </g>
            );
          })}
          <circle cx="0" cy="0" r="46" fill="url(#dbHubGrad)" stroke="#635bff" strokeWidth="4" />
        </svg>
        <button
          className={`db-hub-btn ${idlePulse ? "db-hub-btn--idle" : ""}`}
          onClick={handleSpin}
          disabled={!canSpin}
        >
          {spinning ? "…" : totalSpins > 0 ? L.dailyBonus.spin : "—"}
        </button>
      </div>

      {/* Monthly earnings */}
      <div className="db-earned">
        <div className="db-earned-label">{L.dailyBonus.earnedThisMonth}</div>
        <div className="db-earned-amount">${status.monthly.claimed.toFixed(2)}</div>
      </div>

      {/* Buy more spins */}
      <div className="db-buy-row">
        <button className="db-buy-card" onClick={() => openBuy("single")}>
          <div className="db-buy-icon">🎯</div>
          <div className="db-buy-name">{L.dailyBonus.oneExtraSpin}</div>
          <div className="db-buy-price">${status.pricing.single.price}</div>
          <div className="db-buy-cta">{L.dailyBonus.buyNow}</div>
        </button>
        <button className="db-buy-card db-buy-card--pack" onClick={() => openBuy("pack")}>
          <span className="db-buy-save-pill">
            {L.dailyBonus.saveAmount((status.pricing.single.price * 5 - status.pricing.pack.price).toFixed(0))}
          </span>
          <div className="db-buy-icon">🎁</div>
          <div className="db-buy-name">{L.dailyBonus.fiveSpinsPack}</div>
          <div className="db-buy-price">${status.pricing.pack.price}</div>
          <div className="db-buy-cta">{L.dailyBonus.buyPack}</div>
        </button>
      </div>

      <BuySpinsModal
        show={buyOpen}
        onHide={() => setBuyOpen(false)}
        profile={profile}
        defaultPack={buyDefault}
        onSuccess={() => loadStatus()}
      />


      {/* Win modal */}
      {resultModal && (
        <div className="db-modal-backdrop db-modal-backdrop--show" onClick={() => setResultModal(null)}>
          <div className={`db-modal db-modal--${resultModal.rarity}`} onClick={(e) => e.stopPropagation()}>
            {(resultModal.rarity === "legendary" || resultModal.rarity === "epic") && (
              <div className="db-modal-sparkles" aria-hidden>
                {Array.from({ length: 8 }).map((_, i) => (
                  <span key={i} style={{
                    top: `${10 + Math.random() * 80}%`,
                    left: `${5 + Math.random() * 90}%`,
                    animationDelay: `${i * 0.18}s`,
                  }}>✨</span>
                ))}
              </div>
            )}
            <div className="db-modal-icon">{resultModal.emoji}</div>
            <div className="db-modal-title">{resultModal.title}</div>
            <div className="db-modal-amount">+${resultModal.amount}</div>
            <div className="db-modal-sub">{L.dailyBonus.addedToBalance}</div>
            <button className="db-modal-continue" onClick={() => setResultModal(null)}>{L.dailyBonus.continue}</button>
          </div>
        </div>
      )}

      <style>{`
        .db-stage {
          max-width: 560px; margin: 0 auto; padding: 24px 16px 56px; text-align: center;
          font-family: 'Fredoka', system-ui, sans-serif; color: #1a1f36;
          position: relative;
        }
        .db-stage.db-shake { animation: dbShake 0.32s ease-out; }
        @keyframes dbShake {
          0%, 100% { transform: translate(0,0); }
          15% { transform: translate(-6px, 2px); }
          30% { transform: translate(6px, -3px); }
          45% { transform: translate(-4px, 3px); }
          60% { transform: translate(4px, -2px); }
          75% { transform: translate(-2px, 1px); }
        }
        .db-heading {
          font-family: 'Baloo 2', 'Fredoka', sans-serif; font-weight: 800;
          font-size: clamp(28px, 5vw, 40px); margin: 0 0 6px;
          background: linear-gradient(135deg, #635bff, #7c3aed);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .db-sub { color: #425466; margin: 0 0 18px; }
        .db-badges-row {
          display: flex; justify-content: center; gap: 10px;
          flex-wrap: wrap; margin-bottom: 28px;
        }
        .db-badge-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 16px 8px 6px; background: #f0ebff;
          border: 2px solid #e1d8ff; border-radius: 999px;
          font-weight: 700; color: #4b25ea; font-size: 14px;
          box-shadow: 0 4px 14px -6px rgba(99,91,255,0.3);
        }
        .db-badge-pill--free { background: #dcfce7; border-color: #bbf7d0; color: #166534; }
        .db-badge-pill--muted { background: #f1f5f9; border-color: #e2e8f0; color: #64748b; }
        .db-locked-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border: 1px solid #fcd34d;
          border-radius: 14px;
          padding: 12px 14px;
          margin: 0 auto 22px;
          max-width: 420px;
          box-shadow: 0 4px 12px -4px rgba(245,166,35,0.25);
        }
        .db-locked-icon {
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(255,255,255,0.6);
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; flex-shrink: 0;
        }
        .db-locked-text {
          flex: 1; min-width: 0; text-align: left; line-height: 1.3;
        }
        .db-locked-title {
          font-weight: 800; font-size: 14px; color: #78350f;
        }
        .db-locked-sub {
          font-size: 12px; color: #92400e; margin-top: 2px;
        }
        a.db-locked-cta, a.db-locked-cta:visited, a.db-locked-cta:hover, a.db-locked-cta:active {
          flex-shrink: 0; padding: 8px 14px;
          background: linear-gradient(135deg, #f59e0b, #b45309);
          color: #fff !important; font-weight: 700; font-size: 13px;
          border-radius: 999px; text-decoration: none;
          box-shadow: 0 4px 10px -2px rgba(245,158,11,0.4);
        }
        a.db-locked-cta:hover { opacity: 0.92; }
        @media (max-width: 380px) {
          .db-locked-banner { gap: 10px; padding: 10px 12px; }
          .db-locked-icon { width: 32px; height: 32px; font-size: 14px; }
          .db-locked-title { font-size: 13px; }
          .db-locked-sub { font-size: 11.5px; }
          .db-locked-cta { padding: 7px 12px; font-size: 12px; }
        }
        .db-count {
          background: linear-gradient(135deg, #635bff, #7c3aed); color: #fff;
          min-width: 28px; height: 26px; padding: 0 10px; border-radius: 999px;
          display: inline-flex; align-items: center; justify-content: center;
          font-family: 'Baloo 2', sans-serif; font-weight: 800; font-size: 13px;
        }
        .db-count--free { background: #16a34a; }

        .db-wheel-wrap {
          position: relative; width: min(380px, 90vw); aspect-ratio: 1;
          margin: 0 auto 28px;
        }
        .db-wheel-wrap--idle::before {
          content: ""; position: absolute; inset: -20px; border-radius: 50%;
          background: radial-gradient(circle, rgba(99,91,255,0.25), transparent 70%);
          animation: dbHalo 2.5s ease-in-out infinite;
          z-index: 0; pointer-events: none;
        }
        @keyframes dbHalo {
          0%, 100% { transform: scale(0.95); opacity: 0.6; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        .db-bezel {
          position: absolute; inset: -6px; border-radius: 50%;
          background: linear-gradient(135deg, #fff 0%, #e0d7ff 100%);
          box-shadow: 0 8px 30px rgba(99,91,255,0.25), inset 0 -3px 10px rgba(99,91,255,0.15);
          z-index: 1;
        }
        .db-stud {
          position: absolute; top: 50%; left: 50%;
          width: 8px; height: 8px; border-radius: 50%;
          background: radial-gradient(circle at 35% 35%, #ffd24a, #b8730d);
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          margin-left: -4px; margin-top: -4px;
        }
        .db-pointer {
          position: absolute; top: -10px; left: 50%;
          transform: translateX(-50%); z-index: 6;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.35));
        }
        .db-wheel {
          position: relative; z-index: 2;
          width: 100%; height: 100%; display: block;
        }
        .db-hub-btn {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 92px; height: 92px; border-radius: 50%;
          border: none; cursor: pointer;
          background: linear-gradient(135deg, #635bff, #7c3aed);
          color: #fff; font-weight: 800; font-family: 'Baloo 2', sans-serif;
          font-size: 22px; letter-spacing: 1px;
          box-shadow: 0 8px 24px rgba(99,91,255,0.45), inset 0 -3px 8px rgba(0,0,0,0.2);
          transition: transform 0.15s, box-shadow 0.15s;
          z-index: 3;
        }
        .db-hub-btn:not(:disabled):hover { transform: translate(-50%, -50%) scale(1.06); }
        .db-hub-btn:not(:disabled):active { transform: translate(-50%, -50%) scale(0.96); }
        .db-hub-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .db-hub-btn--idle::after {
          content: ""; position: absolute; inset: -8px; border-radius: 50%;
          border: 3px solid rgba(99,91,255,0.5);
          animation: dbBtnPulse 1.6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes dbBtnPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.15); opacity: 0; }
        }

        .db-earned {
          background: #fff; border: 1px solid #e3e8ee; border-radius: 14px;
          padding: 14px 16px; margin-bottom: 22px; text-align: center;
          box-shadow: 0 2px 6px rgba(50,50,93,0.04);
        }
        .db-earned-label {
          font-size: 11px; color: #8898aa; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.08em;
        }
        .db-earned-amount {
          font-family: 'Baloo 2', sans-serif; font-weight: 800;
          font-size: 28px; margin-top: 2px;
          background: linear-gradient(135deg, #635bff, #d345c6);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .db-buy-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .db-buy-card {
          position: relative;
          background: #fff; border: 2px solid #e3e8ee; border-radius: 16px;
          padding: 18px 14px 14px; cursor: pointer; text-align: center;
          transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .db-buy-save-pill {
          position: absolute; top: -10px; right: -8px;
          padding: 4px 10px;
          background: linear-gradient(135deg, #16a34a, #15803d);
          color: #fff; font-weight: 800; font-size: 0.65rem;
          letter-spacing: 0.02em; border-radius: 999px;
          box-shadow: 0 4px 10px -3px rgba(22,163,74,0.45);
          white-space: nowrap;
        }
        .db-buy-card:not(:disabled):hover {
          transform: translateY(-2px); border-color: #635bff;
          box-shadow: 0 8px 20px rgba(99,91,255,0.15);
        }
        .db-buy-card--pack {
          background: linear-gradient(160deg, #fff7e6, #ffe7b3);
          border-color: #f5a623; color: #5a3a00;
        }
        .db-buy-icon { font-size: 32px; margin-bottom: 6px; }
        .db-buy-name { font-weight: 800; font-size: 15px; margin-bottom: 4px; }
        .db-buy-price { font-family: 'Baloo 2', sans-serif; font-weight: 800; font-size: 22px; color: #1a1f36; }
        .db-buy-card--pack .db-buy-price { color: #5a3a00; }
        .db-buy-cta {
          margin-top: 10px; padding: 7px 0; background: #635bff; color: #fff;
          border-radius: 999px; font-weight: 700; font-size: 13px;
        }
        .db-buy-card--pack .db-buy-cta { background: linear-gradient(135deg, #f5a623, #b8730d); }
        .db-buy-card:disabled { opacity: 0.5; cursor: wait; }

        .db-modal-backdrop {
          position: fixed; inset: 0; background: rgba(20,15,40,0.55);
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 16px;
          opacity: 0; pointer-events: none; transition: opacity 0.3s ease;
        }
        .db-modal-backdrop--show { opacity: 1; pointer-events: auto; }
        .db-modal {
          background: #fff; border-radius: 28px; padding: 36px 28px 26px;
          width: 100%; max-width: 380px; text-align: center;
          box-shadow: 0 30px 80px -20px rgba(75,37,234,0.45);
          position: relative; overflow: hidden;
          animation: dbModalPop 0.45s cubic-bezier(.16,.85,.3,1.05);
        }
        .db-modal--rare {
          background: linear-gradient(160deg, #f8f7ff, #efeaff);
          box-shadow: 0 30px 80px -20px rgba(99,91,255,0.4);
        }
        .db-modal--epic {
          background: linear-gradient(160deg, #ffeef7, #ffe2ed);
          box-shadow: 0 30px 80px -20px rgba(211,69,198,0.45);
        }
        .db-modal--legendary {
          background: linear-gradient(160deg, #fffaeb, #fff0c4);
          box-shadow: 0 30px 80px -20px rgba(245,166,35,0.55);
          border: 2px solid #f5a623;
        }
        @keyframes dbModalPop {
          from { opacity: 0; transform: scale(0.85) translateY(30px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .db-modal-sparkles { position: absolute; inset: 0; pointer-events: none; }
        .db-modal-sparkles span {
          position: absolute; font-size: 20px; opacity: 0;
          animation: dbSparkle 2.4s ease-in-out infinite;
        }
        @keyframes dbSparkle {
          0%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.1) rotate(20deg); }
        }
        .db-modal-icon {
          font-size: 64px; margin-bottom: 8px;
          animation: dbIconWiggle 0.6s ease-in-out;
        }
        @keyframes dbIconWiggle {
          0%, 100% { transform: rotate(0); }
          25% { transform: rotate(-10deg) scale(1.1); }
          75% { transform: rotate(10deg) scale(1.1); }
        }
        .db-modal-title {
          font-family: 'Baloo 2', sans-serif; font-weight: 800;
          font-size: 24px; margin-bottom: 8px; color: #1a1f36;
        }
        .db-modal--legendary .db-modal-title { color: #b8730d; }
        .db-modal--epic .db-modal-title { color: #c41e3a; }
        .db-modal-amount {
          font-family: 'Baloo 2', sans-serif; font-weight: 900;
          font-size: 64px; line-height: 1;
          background: linear-gradient(135deg, #635bff, #d345c6);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          margin: 6px 0;
        }
        .db-modal--legendary .db-modal-amount {
          background: linear-gradient(135deg, #f5a623, #b8730d);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          text-shadow: 0 2px 10px rgba(245,166,35,0.3);
        }
        .db-modal-sub { color: #425466; font-size: 14px; margin-bottom: 22px; }
        .db-modal-continue {
          width: 100%; padding: 14px; border: none; border-radius: 14px;
          background: linear-gradient(135deg, #635bff, #7c3aed); color: #fff;
          font-weight: 800; font-size: 16px; cursor: pointer;
          font-family: 'Baloo 2', sans-serif;
          box-shadow: 0 8px 18px -6px rgba(99,91,255,0.5);
        }
        .db-modal--legendary .db-modal-continue {
          background: linear-gradient(135deg, #f5a623, #b8730d);
          box-shadow: 0 8px 18px -6px rgba(245,166,35,0.55);
        }
      `}</style>
    </div>
  );
}

export default DailyBonus;
